import { computed, Directive, inject, OnDestroy, signal } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";

import { Subject, takeUntil } from "rxjs";
import {
  LktProfileSelectionModalComponent,
  NavbarMenuItemType,
  ProfileSelectionItem,
  RouterItemType,
  UserPopoverItem
} from "@frontends-v3/design-system";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import {
  buildUserPopoverItems,
  Constants,
  ProfileConstants,
  CustomJwtPayload,
  UserProfilesResponse,
  SelectProfileResponse,
  LektoAuthService,
  HelpCenterStateService,
  IdentityService,
  ImpersonationService,
  ProductConfigService,
  ProductDetectorService,
  UserAvatarStateService,
  resolveTargetAppUrl
} from "@frontends-v3/shared-core";

import { OFFLINE_MODE_ACTIVATOR } from "../../offline/offline-mode-activator.token";
import { OfflineModeStateService } from "../../offline/offline-mode-state.service";
import { OfflineModeService } from "../../offline/offline-mode.service";
import { featureAvailability } from "../../offline/offline-policy.util";

@Directive({ standalone: true })
export abstract class UserAccountBaseDirective implements OnDestroy {
  protected readonly router = inject(Router);
  protected readonly authService = inject(LektoAuthService);
  protected readonly identityService = inject(IdentityService);
  protected readonly productDetector = inject(ProductDetectorService);
  protected readonly productConfig = inject(ProductConfigService);
  protected readonly modalService = inject(NzModalService);
  protected readonly message = inject(NzMessageService);
  protected readonly helpCenterState = inject(HelpCenterStateService);
  protected readonly avatarState = inject(UserAvatarStateService);
  protected readonly impersonationService = inject(ImpersonationService);
  protected readonly offlineState = inject(OfflineModeStateService);
  protected readonly offlineMode = inject(OfflineModeService);
  // DEV-966: só o app que sabe escolher a atribuição (platform) fornece o ativador; sem ele o
  // item "Modo Offline" nem aparece.
  private readonly offlineActivator = inject(OFFLINE_MODE_ACTIVATOR, { optional: true });
  protected readonly destroy$ = new Subject<void>();
  private readonly isSwitchingProfile = signal(false);

  public readonly hasMultipleProfiles = signal<boolean>(false);

  /**
   * CA04: a sessão personificada não troca de perfil. O enforcement real é do servidor, que emite o
   * token com um papel só — isto é o lado visível, para o usuário não ver um controle inerte.
   * Cobre de uma vez user-popover, user-bottom-sheet, sidebar-menu e app-menu, que herdam daqui.
   */
  public readonly canSwitchProfile = computed<boolean>(
    () => !this.impersonationService.isImpersonating() && this.hasMultipleProfiles()
  );

  public readonly userInfo = computed<CustomJwtPayload | undefined>(() =>
    this.authService.getUserInfoFromToken()
  );

  public readonly userName = computed<string>(() => {
    const info = this.userInfo();
    return info ? info.name : "Usuário";
  });

  public readonly userAvatar = computed<string>(() => {
    const fromState = this.avatarState.avatarUrl();
    if (fromState === null) {
      const info = this.userInfo();
      return info ? info.picture : "";
    }
    return fromState;
  });

  /**
   * Sessão sem cookie de papel é o estado real da aba aberta pelo link de acesso, onde
   * `createAuthCookies` apaga os cookies do domínio e grava só o token. A guarda de `null` dos dois
   * computed abaixo existe por causa dele.
   *
   * Até a DEV-991 esse estado fazia `getProfileData()` LANÇAR (`JSON.parse` do cookie vazio, sem
   * guarda), e a guarda aqui era um `try/catch`. A garantia passou para a origem — o serviço devolve
   * `null` e nunca lança —, então sobrou o que sempre importou: tratar a AUSÊNCIA de perfil. Um
   * `try/catch` teria virado defesa de um ramo que o tipo declara inalcançável.
   *
   * Continua sendo obrigatório não falhar aqui: estes valores são lidos durante a renderização do
   * menu, então desreferenciar `null` não degradaria um rótulo — derrubaria o bloco de atualização
   * do `lkt-sidebar-menu` inteiro, e com ele o alternador de visão.
   *
   * Sem o cookie o papel ainda é conhecido: `authProfile()` o resolve pela claim do token. Por isso
   * o fallback nomeia o perfil em vez de cair em "Usuário" — quem personifica um gestor por link
   * precisa enxergar em que papel está.
   */
  public readonly profileLabel = computed<string>(() => {
    const profileData = this.readCurrentProfile();
    if (!profileData) {
      return ProfileConstants.getProfileLabel(this.authService.authProfile(), "Usuário");
    }
    return ProfileConstants.getProfileLabel(
      profileData.coRole,
      profileData.txProfileType || "Usuário"
    );
  });

  public readonly profileIcon = computed<string>(() => {
    const profileData = this.readCurrentProfile();
    if (!profileData) return ProfileConstants.getProfileIcon(this.authService.authProfile());
    return ProfileConstants.getProfileIcon(profileData.coRole);
  });

  private static readonly GESTOR_ROLES: ReadonlySet<string> = new Set([
    Constants.PROFILES.NetworkManager,
    Constants.PROFILES.SubNetworkManager,
    Constants.PROFILES.SchoolManager
  ]);

  public readonly isGestorProfile = computed<boolean>(() =>
    UserAccountBaseDirective.GESTOR_ROLES.has(this.authService.authProfile())
  );

  /**
   * CA01/CA02 (DEV-919): o alternador de visão é exclusivo dos papéis de gestão — são eles que
   * trabalham nos dois sistemas. Para o Administrador Lekto o controle é obsoleto e não é exibido.
   *
   * Não é contenção de acesso, e sim remoção de um controle sem propósito para o perfil: o admin não
   * usa a Plataforma, e quem o devolve ao Admin quando ele chega lá por deep link é o
   * `appProfileGuard` do shell, não este controle. Endurecer isto como gate de autorização é ler a
   * regra errada — a autoridade sobre acesso é do servidor.
   */
  public readonly canSwitchSystems = computed<boolean>(() => this.isGestorProfile());

  // DEV-1104: o Modo Offline não existe para o leitor (Aluno, Família) — o item some mesmo na Plataforma.
  public readonly userPopoverItems = computed<UserPopoverItem[]>(() =>
    buildUserPopoverItems(this.helpCenterState.links(), {
      hasError: this.helpCenterState.hasError(),
      offline:
        this.offlineActivator && !ProfileConstants.isReaderProfile(this.authService.authProfile())
          ? { enabled: this.offlineState.enabled() }
          : null
    })
  );

  public readonly helpMenuItems = computed<NavbarMenuItemType[]>(() => {
    // DEV-966 (CA 09): com o modo ligado a Central de Ajuda fica visível, mas indisponível.
    const helpPaused = featureAvailability("help", this.offlineState.enabled()) !== "allowed";
    const links = this.helpCenterState.links();
    const hasError = this.helpCenterState.hasError();
    const buildRoute = (url: string | null): RouterItemType["route"] => url ?? "";
    const isDisabled = (url: string | null): boolean => helpPaused || hasError || !url;

    return [
      {
        id: "help",
        label: "Central de Ajuda",
        icon: "help",
        hasGroup: true,
        routeGroup: [
          {
            id: "faq",
            route: buildRoute(links.faq),
            label: "Dúvidas Frequentes",
            disabled: isDisabled(links.faq)
          },
          {
            id: "specialist",
            route: buildRoute(links.specialist),
            label: "Especialista Lekto",
            disabled: isDisabled(links.specialist)
          },
          {
            id: "support",
            route: buildRoute(links.support),
            label: "Abrir um Chamado",
            disabled: isDisabled(links.support)
          }
        ]
      }
    ];
  });

  constructor() {
    this.identityService
      .getUserProfiles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profiles: UserProfilesResponse[]) =>
          this.hasMultipleProfiles.set(profiles.length > 1),
        error: () => this.hasMultipleProfiles.set(false)
      });
  }

  public onPopoverItemClick(itemId: string): void {
    if (itemId === "logout") {
      this.authService.logout();
      return;
    }

    if (itemId === "offline-mode") {
      this.onOfflineModeToggle();
      return;
    }

    const item = this.userPopoverItems().find((entry: UserPopoverItem) => entry.id === itemId);
    if (!item || item.disabled) return;

    if (item.externalUrl) {
      globalThis.open(item.externalUrl, "_blank", "noopener,noreferrer");
    } else if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  public onHelpMenuExpand(): void {
    this.helpCenterState.retryIfErrored();
  }

  /**
   * DEV-966 (CA 01): ligar exige escolher a atribuição antes — o app abre a janela; desligar é
   * imediato e não passa por confirmação.
   */
  public onOfflineModeToggle(): void {
    if (this.offlineState.enabled()) {
      this.offlineMode
        .disable()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.message.success("Modo Offline desligado."),
          error: () =>
            this.message.error("Não foi possível desligar o modo Offline. Tente novamente.")
        });
      return;
    }
    this.offlineActivator?.requestEnable();
  }

  public onSwitchProfile(): void {
    if (this.isSwitchingProfile()) return;

    this.isSwitchingProfile.set(true);
    this.identityService
      .getUserProfiles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profiles: UserProfilesResponse[]) => {
          this.isSwitchingProfile.set(false);
          if (profiles.length === 0) {
            console.warn("[UserAccountBase] Nenhum perfil disponível para troca.");
            return;
          }
          this.showProfileSelectionModal(profiles);
        },
        error: (err: unknown) => {
          this.isSwitchingProfile.set(false);
          console.warn("[UserAccountBase] Erro ao carregar perfis:", err);
          this.message.error("Erro ao carregar perfis disponíveis.");
        }
      });
  }

  private showProfileSelectionModal(userProfiles: UserProfilesResponse[]): void {
    const visibleProfiles = this.filterProfilesByTokenRoles(userProfiles);
    const currentProfile = this.readCurrentProfile();
    const profiles = ProfileConstants.mapProfilesToSelectionItems(visibleProfiles, currentProfile);

    const modalRef = this.modalService.create({
      nzTitle: "",
      nzContent: LktProfileSelectionModalComponent,
      nzData: {
        profiles,
        onProfileSelected: (selected: ProfileSelectionItem) => {
          const profile = visibleProfiles.find(
            (p) => ProfileConstants.buildProfileKey(p) === selected.id
          );
          if (!profile) return;
          this.handleProfileSelected(profile, modalRef);
        }
      },
      nzFooter: null,
      nzClosable: false,
      nzMask: true,
      nzMaskClosable: true,
      nzWidth: 528,
      nzCentered: true
    });
  }

  private handleProfileSelected(
    profile: UserProfilesResponse,
    modalRef: NzModalRef<LktProfileSelectionModalComponent>
  ): void {
    const instance = modalRef.getContentComponent();
    const product = this.productDetector.detectProduct();
    if (!product) {
      instance?.setLoading(false);
      this.message.error("Erro ao identificar o produto. Recarregue a página e tente novamente.");
      return;
    }

    instance?.setLoading(true);

    this.identityService
      .selectProfile({
        idProfileType: profile.idProfileType,
        idSchool: profile.idSchool,
        idNetwork: profile.idNetwork,
        product
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: SelectProfileResponse) => {
          this.authService.createProfileCookie(profile, response);
          globalThis.location.href = resolveTargetAppUrl(response.targetApp, this.productConfig);
        },
        error: (err: unknown) => {
          instance?.setLoading(false);
          console.warn("[UserAccountBase] Erro ao selecionar perfil:", err);
          this.message.error(this.resolveSelectProfileErrorMessage(err));
        }
      });
  }

  /**
   * `getProfileData()` passou a devolver `null` em vez de lançar (DEV-991), então o `try/catch` que
   * vivia aqui virou letra morta e saiu. O método permanece como nome do conceito "perfil ativo,
   * se houver" para os dois `computed` acima.
   */
  private readCurrentProfile(): UserProfilesResponse | null {
    return this.authService.getProfileData();
  }

  /**
   * Mantém apenas os perfis cujo coRole está presente no claim `role` do token
   * atual. Ao personificar, o token não traz a role `admin`, então o perfil
   * "Administrador Lekto" some da modal de troca de perfil.
   */
  private filterProfilesByTokenRoles(profiles: UserProfilesResponse[]): UserProfilesResponse[] {
    const role = this.authService.getUserInfoFromToken()?.role;
    let tokenRoles: string[];
    if (Array.isArray(role)) {
      tokenRoles = role;
    } else if (role) {
      tokenRoles = [role];
    } else {
      tokenRoles = [];
    }
    return profiles.filter((profile) => tokenRoles.includes(profile.coRole));
  }

  private resolveSelectProfileErrorMessage(err: unknown): string {
    const httpError = err instanceof HttpErrorResponse ? err : null;
    const backendMessage =
      typeof httpError?.error?.message === "string" ? httpError.error.message : null;

    if (httpError?.status === 401) {
      return "Sua sessão expirou. Faça login novamente.";
    }

    if (httpError?.status === 422) {
      return (
        backendMessage ??
        "Não foi possível trocar para este perfil. Verifique o vínculo e escolha outro perfil."
      );
    }

    if (httpError?.status === 0) {
      return "Sem conexão. Verifique sua internet e tente novamente.";
    }

    return "Não foi possível trocar de perfil. Tente novamente ou escolha outro perfil.";
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
